// ============================================================================
// SALES TRANSACTION SERVICE - Phase 4 Compliant
// ============================================================================

import type {
  SalesTransaction,
  CreateSalesTransactionRequest,
  UpdateSalesTransactionRequest,
  SalesFilters,
  CommissionCalculation,
  EnhancedCommissionCalculation,
  AccountManagerCommissionSplits
} from '@flowstate/types';
import type { ServiceResponse, IService } from '@/services/core/service-registry';
import type { Json } from '@/integrations/supabase/types';
import { typedSupabase } from '@/lib/typed-supabase';
import { clientService } from '@/services/core/client.service';
import { logger } from "@/lib/logger";
import { getCurrentUserId } from './current-user';

// ============================================================================
// SERVICE INTERFACE
// ============================================================================

export interface ISalesTransactionService extends IService {
  getSalesTransactions(filters?: SalesFilters): Promise<ServiceResponse<SalesTransaction[]>>;
  createSalesTransaction(request: CreateSalesTransactionRequest): Promise<ServiceResponse<SalesTransaction>>;
  updateSalesTransaction(transactionId: string, request: UpdateSalesTransactionRequest): Promise<ServiceResponse<SalesTransaction>>;
}

// ============================================================================
// SALES TRANSACTION SERVICE
// ============================================================================

export class SalesTransactionService implements ISalesTransactionService {
  readonly name = 'SalesTransactionService';
  private static instance: SalesTransactionService;
  private commissionCalculationService?: {
    calculateCommission(teamId: string, transactionType: string, amount: number): Promise<ServiceResponse<CommissionCalculation>>;
    calculateTransactionCommissions(clientId: string, teamId: string, transactionType: string, amount: number, salesRepId?: string, closerId?: string): Promise<ServiceResponse<EnhancedCommissionCalculation>>;
  };

  private constructor() {}

  static getInstance(): SalesTransactionService {
    if (!SalesTransactionService.instance) {
      SalesTransactionService.instance = new SalesTransactionService();
    }
    return SalesTransactionService.instance;
  }

  /**
   * Set commission calculation service dependency
   */
  setCommissionCalculationService(service: {
    calculateCommission(teamId: string, transactionType: string, amount: number): Promise<ServiceResponse<CommissionCalculation>>;
    calculateTransactionCommissions(clientId: string, teamId: string, transactionType: string, amount: number, salesRepId?: string, closerId?: string): Promise<ServiceResponse<EnhancedCommissionCalculation>>;
  }): void {
    this.commissionCalculationService = service;
  }

  async initialize(): Promise<void> {
    logger.info('🔧 Initializing SalesTransactionService...');
  }

  async dispose(): Promise<void> {
    logger.info('🧹 Disposing SalesTransactionService...');
  }

  /**
   * Get sales transactions with optional filters
   */
  async getSalesTransactions(filters?: SalesFilters): Promise<ServiceResponse<SalesTransaction[]>> {
    try {
      const userId = await getCurrentUserId();

      if (!userId) {
        return { success: false, error: 'User not authenticated' };
      }

      let query = typedSupabase
        .from('sales_transactions')
        .select(`
          *,
          team:sales_teams!sales_transactions_team_id_fkey(id, name),
          client:clients!sales_transactions_client_id_fkey(id, name, email),
          opportunity:opportunities!sales_transactions_opportunity_id_fkey(id, title, value),
          affiliate_link:affiliate_links!sales_transactions_affiliate_link_id_fkey(id, code),
          sales_rep:profiles!sales_transactions_sales_rep_id_fkey(id, full_name, username, email),
          closer:profiles!sales_transactions_closer_id_fkey(id, full_name, username, email),
          created_by_user:profiles!sales_transactions_created_by_fkey(id, full_name, username, email)
        `);

      // Apply filters
      if (filters?.team_id) {
        query = query.eq('team_id', filters.team_id);
      }
      if (filters?.project_id) {
        query = query.eq('project_id', filters.project_id);
      }
      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status);
      }
      if (filters?.transaction_type && filters.transaction_type.length > 0) {
        query = query.in('transaction_type', filters.transaction_type);
      }
      if (filters?.user_id) {
        query = query.or(`sales_rep_id.eq.${filters.user_id},closer_id.eq.${filters.user_id}`);
      }
      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }
      if (filters?.amount_min !== undefined) {
        query = query.gte('amount', filters.amount_min);
      }
      if (filters?.amount_max !== undefined) {
        query = query.lte('amount', filters.amount_max);
      }
      if (filters?.commission_min !== undefined) {
        query = query.gte('commission_amount', filters.commission_min);
      }
      if (filters?.commission_max !== undefined) {
        query = query.lte('commission_amount', filters.commission_max);
      }

      // Sorting - default to created_at desc
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: (data as SalesTransaction[]) || [] };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get sales transactions'
      };
    }
  }

  /**
   * Create sales transaction
   */
  async createSalesTransaction(request: CreateSalesTransactionRequest): Promise<ServiceResponse<SalesTransaction>> {
    try {
      const userId = (await typedSupabase.auth.getUser()).data.user?.id;

      if (!userId) {
        return { success: false, error: 'User not authenticated' };
      }

      // Calculate commission with account manager splits (if client_id provided)
      let commissionSplits: AccountManagerCommissionSplits = (request.commission_splits as AccountManagerCommissionSplits) || {};
      let commissionAmount = 0;
      let commissionRate = 0;

      if (request.client_id && this.commissionCalculationService) {
        // Use enhanced commission calculation for account manager splits
        const enhancedCalculation = await this.commissionCalculationService.calculateTransactionCommissions(
          request.client_id,
          request.team_id,
          request.transaction_type,
          request.amount,
          request.sales_rep_id,
          request.closer_id
        );

        if (enhancedCalculation.data) {
          commissionSplits = enhancedCalculation.data.split_details;
          commissionAmount = enhancedCalculation.data.commission_amount;
          commissionRate = enhancedCalculation.data.total_rate;
        } else if (this.commissionCalculationService.calculateCommission) {
          // Fallback to basic calculation
          const basicCalc = await this.commissionCalculationService.calculateCommission(
            request.team_id,
            request.transaction_type,
            request.amount
          );
          if (basicCalc.data) {
            commissionAmount = basicCalc.data.commission_amount;
            commissionRate = basicCalc.data.commission_rate;
          }
        }
      } else if (this.commissionCalculationService?.calculateCommission) {
        // No client - use basic calculation
        const basicCalc = await this.commissionCalculationService.calculateCommission(
          request.team_id,
          request.transaction_type,
          request.amount
        );
        if (basicCalc.data) {
          commissionAmount = basicCalc.data.commission_amount;
          commissionRate = basicCalc.data.commission_rate;
        }
      }

      const { data, error } = await typedSupabase
        .from('sales_transactions')
        .insert({
          team_id: request.team_id,
          opportunity_id: request.opportunity_id,
          affiliate_link_id: request.affiliate_link_id,
          client_id: request.client_id,
          project_id: request.project_id,
          transaction_type: request.transaction_type,
          amount: request.amount,
          commission_amount: commissionAmount,
          commission_rate: commissionRate,
          sales_rep_id: request.sales_rep_id,
          closer_id: request.closer_id,
          description: request.description,
          notes: request.notes,
          metadata: request.metadata ?? {},
          commission_splits: commissionSplits as unknown as Json,
          created_by: userId
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // 🚀 AUTO-LOG: Activity for client
      try {
        if (request.client_id) {
          await clientService.createClientActivity({
            client_id: request.client_id,
            type: 'payment',
            title: 'New Transaction Recorded',
            description: `Sales transaction recorded: ${request.transaction_type} (Amount: ${request.amount})`,
            metadata: {
              transaction_id: data.id,
              transaction_type: request.transaction_type,
              amount: request.amount
            }
          });
        }
      } catch (logError) {
        logger.warn("Failed to auto-log sales transaction creation:", { error: logError });
      }

      return { success: true, data: data as SalesTransaction };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create sales transaction'
      };
    }
  }

  /**
   * Update sales transaction with field-level permission enforcement
   */
  async updateSalesTransaction(transactionId: string, request: UpdateSalesTransactionRequest): Promise<ServiceResponse<SalesTransaction>> {
    try {
      // Authentication check
      const userId = (await typedSupabase.auth.getUser()).data.user?.id;

      if (!userId) {
        return { success: false, error: 'User not authenticated' };
      }

      // Get transaction with team information for permission check
      const { data: transaction, error: fetchError } = await typedSupabase
        .from('sales_transactions')
        .select(`
          *,
          team:sales_teams!sales_transactions_team_id_fkey(
            id,
            manager_id,
            name
          )
        `)
        .eq('id', transactionId)
        .single();

      if (fetchError || !transaction) {
        return { success: false, error: 'Sales transaction not found' };
      }

      const team = (transaction as { team: { id: string; manager_id: string; name: string } | null }).team;
      const isManager = team ? team.manager_id === userId : false;
      const isAssigned = transaction.sales_rep_id === userId || transaction.closer_id === userId;

      // Define manager-only fields that require elevated permissions
      const managerOnlyFields = [
        'status',
        'commission_amount',
        'commission_rate',
        'approved_by',
        'approved_at',
        'paid_at',
        'team_id',
        'affiliate_link_id',
        'amount',
        'commission_splits',
        'sales_rep_id',
        'closer_id'
      ];

      // Check for forbidden field updates by non-managers
      if (!isManager) {
        const attemptedFields = Object.keys(request).filter(key => request[key as keyof UpdateSalesTransactionRequest] !== undefined);
        const forbiddenField = attemptedFields.find(f => managerOnlyFields.includes(f));

        if (forbiddenField) {
          return { success: false, error: `Only managers can update field: ${forbiddenField}` };
        }

        // Non-managers can only update their own assigned transactions
        if (!isAssigned) {
          return { success: false, error: 'You can only update transactions assigned to you' };
        }
      }

      // Build update data (only include fields that were actually provided)
      const updateData: Record<string, unknown> = {};
      
      if (request.amount !== undefined) updateData.amount = request.amount;
      if (request.commission_amount !== undefined) updateData.commission_amount = request.commission_amount;
      if (request.commission_rate !== undefined) updateData.commission_rate = request.commission_rate;
      if (request.status !== undefined) updateData.status = request.status;
      if (request.sales_rep_id !== undefined) updateData.sales_rep_id = request.sales_rep_id;
      if (request.closer_id !== undefined) updateData.closer_id = request.closer_id;
      if (request.project_id !== undefined) updateData.project_id = request.project_id;
      if (request.description !== undefined) updateData.description = request.description;
      if (request.notes !== undefined) updateData.notes = request.notes;
      if (request.metadata !== undefined) updateData.metadata = request.metadata;
      if (request.commission_splits !== undefined) updateData.commission_splits = request.commission_splits;
      if (request.approved_by !== undefined) updateData.approved_by = request.approved_by;
      if (request.dispute_reason !== undefined) updateData.dispute_reason = request.dispute_reason;

      // Set timestamps based on status (manager-only operation)
      if (isManager && request.status === 'approved') {
        updateData.approved_at = new Date().toISOString();
        updateData.approved_by = userId;
      } else if (isManager && request.status === 'paid') {
        updateData.paid_at = new Date().toISOString();
      } else if (isManager && request.status === 'disputed') {
        updateData.disputed_at = new Date().toISOString();
      }

      const { data, error } = await typedSupabase
        .from('sales_transactions')
        .update(updateData as unknown as Partial<{
          amount: number;
          commission_amount: number;
          commission_rate: number;
          status: string;
          sales_rep_id: string | null;
          closer_id: string | null;
          project_id: string | null;
          description: string | null;
          notes: string | null;
          metadata: Json;
          commission_splits: Json;
          approved_by: string | null;
          dispute_reason: string | null;
          approved_at: string | null;
          paid_at: string | null;
          disputed_at: string | null;
          updated_at: string;
        }>)
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      // 🚀 AUTO-LOG: Activity for client on status change
      try {
        if (data.client_id && request.status && request.status !== transaction.status) {
          await clientService.createClientActivity({
            client_id: data.client_id,
            type: 'payment',
            title: 'Transaction Status Updated',
            description: `Transaction status changed from ${transaction.status} to ${request.status}`,
            metadata: {
              transaction_id: transactionId,
              previous_status: transaction.status,
              new_status: request.status
            }
          });
        }
      } catch (logError) {
        logger.warn("Failed to auto-log sales transaction status change:", { error: logError });
      }

      return { success: true, data: data as SalesTransaction };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update sales transaction'
      };
    }
  }
}

// Export singleton instance
export const salesTransactionService = SalesTransactionService.getInstance();



